import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../i18n/t';
import './LopSearchableSelect.css';

// K = the id type. Defaults to number (FK ids); string for text keys such as
// bank account numbers ("1030329739A").
export interface LopSearchableSelectProps<K extends string | number = number> {
  value: K | null;
  options: { id: K; name: string }[];
  onChange: (id: K | null) => void;
  allowNone?: boolean; // nullable FK → offer a "— ללא —" clear option
  noneLabel?: string; // override the default "— ללא —" label (e.g. "(הכל)")
  placeholder?: string;
  disabled?: boolean;
  id?: string; // for the <label htmlFor>
}

const optionLabel = (id: string | number, name: string): string => `${id} - ${name}`;

// Reusable FK dropdown showing "<id> - <name>", filterable by typing
// (client-side; lookups are small). Generic — no domain knowledge.
export function LopSearchableSelect<K extends string | number = number>({
  value,
  options,
  onChange,
  allowNone = false,
  noneLabel,
  placeholder,
  disabled = false,
  id,
}: LopSearchableSelectProps<K>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  // The label for the currently-selected value. If the value is an orphan
  // (not in options), still show its raw id so it stays visible.
  const selectedLabel = useMemo(() => {
    if (value == null) return '';
    const match = options.find((o) => o.id === value);
    return match ? optionLabel(match.id, match.name) : String(value);
  }, [value, options]);

  // When closed, the input shows the selected label; when open, it shows what
  // the user is typing (the filter query).
  const displayValue = open ? query : selectedLabel;

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) =>
      optionLabel(o.id, o.name).toLowerCase().includes(term),
    );
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const handleSelect = (selectedId: K | null) => {
    onChange(selectedId);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="lop-ss" ref={containerRef}>
      <input
        id={id}
        type="text"
        className="lop-ss__input"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={id ? `${id}-listbox` : undefined}
        onFocus={() => {
          if (!disabled) {
            setOpen(true);
            setQuery('');
          }
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
          }
        }}
      />
      {open && !disabled && (
        <ul
          className="lop-ss__list"
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
        >
          {allowNone && (
            <li
              className="lop-ss__option lop-ss__option--none"
              role="option"
              aria-selected={value == null}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(null);
              }}
            >
              {noneLabel ?? t('recordForm.none')}
            </li>
          )}
          {filtered.map((o) => (
            <li
              key={o.id}
              className="lop-ss__option"
              role="option"
              aria-selected={value === o.id}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(o.id);
              }}
            >
              {optionLabel(o.id, o.name)}
            </li>
          ))}
          {filtered.length === 0 && !allowNone && (
            <li className="lop-ss__empty">{t('recordForm.noResults')}</li>
          )}
        </ul>
      )}
    </div>
  );
}
